import ReactDOM from 'react-dom/client';
import React from 'react';
import App from './App';
import { ThemeProvider } from './ThemeContext';
import { BrowserRouter } from 'react-router-dom';

import './i18n/index';
// AB#1972 — self-hosted variable fonts (font-display: swap). Must precede
// global.css so the @font-face rules exist before the stylesheet that
// references the families.
import '@fontsource-variable/inter';
import '@fontsource-variable/noto-sans-arabic';
import './styles/global.css';
// CR-2026-05-05a — SVG flag icons. Replaces Unicode flag emojis which
// don't render on Windows Segoe UI Emoji (renders ISO letters instead).
import 'flag-icons/css/flag-icons.min.css';

// CR-2026-05-07a — Suppress benign third-party errors that CRA's
// react-error-overlay surfaces as fatal red crashes:
//
//   1) "ResizeObserver loop completed with undelivered notifications"
//      — Headless UI Combobox quirk when the filtered dropdown resizes
//      mid-callback; no-op for the user.
//   2) "reCAPTCHA Timeout (b)" — emitted by Google's reCAPTCHA script
//      when an invisible-recaptcha token request hangs (network slow /
//      page closed mid-flight). It's logged from inside Google's
//      gstatic-hosted bundle, NOT from our code. We catch + ignore so
//      the user sees an empty / cleared captcha state and can simply
//      retry — the alternative is a full-screen red error overlay over
//      a working form.
//
// Filtering by exact message keeps real errors visible. References:
// CRA #11099, Headless UI #2997, react-google-recaptcha #233.
const SUPPRESSED_ERROR_MESSAGES = [
    'ResizeObserver loop completed with undelivered notifications',
    'ResizeObserver loop limit exceeded',
    'Timeout (b)',          // reCAPTCHA invisible timeout (matches the leaf message)
    'reCAPTCHA Timeout',    // belt-and-braces in case Google rephrases
];

window.addEventListener('error', (e) => {
    if (
        e.message &&
        SUPPRESSED_ERROR_MESSAGES.some((m) => e.message.includes(m))
    ) {
        e.stopImmediatePropagation();
        e.preventDefault();
    }
});

// reCAPTCHA timeouts can also surface as unhandled Promise rejections
// (Google's executeAsync rejects with the Timeout error after ~2 min).
window.addEventListener('unhandledrejection', (e) => {
    const msg = e.reason?.message || String(e.reason || '');
    if (SUPPRESSED_ERROR_MESSAGES.some((m) => msg.includes(m))) {
        e.preventDefault();
    }
});

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
    <React.StrictMode>
        <BrowserRouter>
            <ThemeProvider>
                <App />
            </ThemeProvider>
        </BrowserRouter>
    </React.StrictMode>
);
